/** Saved diagnoses can repeat inactive children as aggregate campaign findings. */
const offCode = value => /^(?:(?:CAMPAIGN|ASSET_GROUP|AD_GROUP|AD_GROUP_AD|AD_GROUP_CRITERION|AD|KEYWORD|ALL_ADS)_)?(?:PAUSED|REMOVED|ENDED)$/.test(String(value || '').toUpperCase());
const idOf = e => String(e.entity_id || e.asset_group_id || e.ad_group_id || '');
const list = value => Array.isArray(value) ? value : [];

export function inactiveDiagnosticEntityIds(entities = []) {
  const off = new Set();
  for (const e of entities) {
    if ([e.status, e.primary_status, ...list(e.primary_status_reasons), ...list(e.blockers)].some(offCode)) {
      if (idOf(e)) off.add(idOf(e));
    }
  }
  // An enabled ad under a paused ad group is also outside diagnostic scope.
  let changed = true;
  while (changed) {
    changed = false;
    for (const e of entities) {
      const id = idOf(e);
      if (id && !off.has(id) && off.has(String(e.parent_id || ''))) {
        off.add(id);
        changed = true;
      }
    }
  }
  return off;
}

export function findingOnlyConcernsInactiveEntities(finding, entities = []) {
  if (!finding) return false;
  const off = inactiveDiagnosticEntityIds(entities);
  const refs = [...list(finding.affected_entities), ...list(finding.affected_asset_groups),
    ...list(finding.target_ids), finding.target_id, finding.entity_id, finding.asset_group_id]
    .filter(Boolean).map(String);
  if (refs.length && refs.every(id => off.has(id))) return true;
  const title = String(finding.issue || finding.headline || finding.plain_summary || '');
  // Aggregate findings often omit IDs. Match declarations of inactive scope,
  // not proposed pauses or incidental references in evidence for an active fix.
  if (/\bpaused (?:ad groups?|asset groups?|ads|keywords)\b/i.test(title)) return true;
  // Counts of intentionally disabled children are not a campaign defect,
  // including "only one ad remains" cards that merely repackage the pause.
  const titleWithoutNegation = title.replace(/not disapproved/gi, '');
  if (/\b(?:paused|removed|ended)\b/i.test(title)
    && /\b(?:ads?|ad groups?|asset groups?|keywords)\b/i.test(title)
    && !/\bdisapproved\b/i.test(titleWithoutNegation)) return true;
  const evidence = String(finding.why?.evidence || finding.evidence || '');
  if (/\b(?:every|all)\b.{0,100}\bads?\b/i.test(evidence)
    && /(?:every|all)[\s\S]*AD_GROUP_AD_PAUSED/.test(evidence)) return true;
  if (refs.some(id => entities.some(e => idOf(e) === id) && !off.has(id))) return false;
  return /\b(?:are|is|remain|sit) (?:also |currently |all |either )*(?:paused|removed|ended)\b/i.test(title)
    || /\b(?:PAUSED|REMOVED)\b/.test(title) && /\b(?:none can serve|cannot serve|cannot deliver|not serving)\b/i.test(title);
}

export function diagnosticEntities(analysis = {}) {
  return list(analysis.entities || analysis.delivery_diagnosis);
}

export function activeDiagnosticView(diagnosis) {
  const entities = diagnosticEntities(diagnosis);
  const off = inactiveDiagnosticEntityIds(entities);
  const health = diagnosis.health || diagnosis.campaign_health;
  // A mixed aggregate cannot be rewritten into a supported active-only verdict.
  // Keep the independently evidenced active findings instead of this summary.
  const contaminatedHealth = /\b(?:paused|removed|ended)\b/i.test(health?.summary || '');
  const clean = {
    ...diagnosis,
    excludedEntityCount: (diagnosis.excludedEntityCount || 0) + off.size,
    entities: entities.filter(e => !off.has(idOf(e))),
    blockers: list(diagnosis.blockers || diagnosis.blocking_issues)
      .filter(b => !findingOnlyConcernsInactiveEntities(b, entities)),
  };
  if (contaminatedHealth) clean.health = null;
  return clean;
}
